import { defineResource } from '@tnn/core'
import { z } from 'zod'
import { juristicTaxIdSchema, paymentMethods } from './receipt'
import { paySides } from './waybill'

/**
 * tms.refund (ใบคืนเงิน): giving a counter customer back what they paid beyond a bill's freight — its price lowered
 * after the money was taken (`waybillOverpaid`). The receipt that took the money stands; the refund names it. Asked for
 * (submitting it: `requested`), approved by someone who may and did not ask (`approved`), then paid out (`paid`): cash
 * out of the hands of whoever pays it, or a transfer with its reference or slip. Paid, the bill's part is taken down by
 * it. Cancelled with a reason at any step; a paid one puts the cash and the bill back.
 *
 * Freight is exempt from VAT, so the receipt is no tax invoice and this is no credit note: just the money going back.
 *
 * A `wht` refund gives back the 1% a juristic customer withholds, when they paid a receipt in full and bring its 50 ทวิ
 * after (`receiptId`): the bills stay paid, the certificate settles that much of them. Never without the certificate.
 */
export const refundStatuses = ['draft', 'requested', 'approved', 'paid', 'cancelled'] as const
export type RefundStatus = (typeof refundStatuses)[number]
/** Paid beyond a bill's freight, or the tax a customer withheld after paying in full */
export const refundKinds = ['overpaid', 'wht'] as const
export type RefundKind = (typeof refundKinds)[number]

export const refund = defineResource({
  app: 'tms',
  name: 'refund',
  path: 'refunds',
  schema: z.object({
    kind: z.enum(refundKinds).default('overpaid'),
    /** Overpaid: the bill (a `wht` refund: its receipt's first, set by the server) */
    waybillId: z.string().min(1).nullish(),
    /** Whose part was overpaid: the sender's or the receiver's */
    side: z.enum(paySides).nullish(),
    /** Withheld: the receipt paid in full that the 50 ทวิ is for (overpaid: the bill's latest, set by the server) */
    receiptId: z.string().min(1).nullish(),
    /** Withheld: 1% of what the receipt took (set by the server) */
    amount: z.number().positive(),
    method: z.enum(paymentMethods).default('cash'),
    reason: z.string().trim().min(1).max(500),
    /** Who gets it back (the party's name, unless someone else comes for it) */
    payeeName: z.string().trim().max(200).nullish(),
    /** Paying by transfer: its reference… */
    reference: z.string().trim().max(100).nullish(),
    /** …or a photo of its slip */
    slipId: z.string().nullish(),
    /** Withheld: the juristic person's tax ID, the certificate's number and its photo (required) */
    whtTaxId: juristicTaxIdSchema.nullish(),
    whtCertNo: z.string().trim().max(50).nullish(),
    whtCertFileId: z.string().nullish(),
  }),
  fields: {
    number: { readOnly: true, search: true, list: { width: 150, priority: 1 } },
    kind: { readOnly: true, filter: true, list: { width: 150, priority: 2 } },
    waybillId: {
      type: 'ref',
      ref: { resource: 'tms.waybill' },
      search: true,
      filter: true,
      list: { width: 150, priority: 1 },
      form: { span: 6 },
    },
    side: { list: { width: 110, priority: 3 }, form: { span: 6 } },
    /** Where the bill's money was taken from that party (server-set from the bill): who may approve and pay it */
    branchId: { readOnly: true, type: 'ref', ref: { resource: 'company.branch' }, filter: true, list: { priority: 3 } },
    /** The receipt that took the money */
    receiptId: { readOnly: true, type: 'ref', ref: { resource: 'tms.receipt' }, filter: true, list: { priority: 3, hidden: true } },
    payeeName: { search: true, list: { priority: 2 }, form: { span: 6 } },
    amount: { type: 'money', list: { width: 130, align: 'end', priority: 1 }, form: { span: 3 } },
    method: { filter: true, list: { width: 110, priority: 2 }, optionLabels: 'tms:receipt.options.method', form: { span: 3 } },
    reason: { type: 'textarea', list: { priority: 2 }, form: { span: 12 } },
    reference: { list: false, form: { span: 6, section: 'payout' } },
    slipId: { type: 'file', list: false, form: { span: 6, section: 'payout' }, import: false, export: false },
    // Asked from the receipt; shown beside the refund
    whtTaxId: { type: 'taxId', list: false, form: { hidden: true } },
    whtCertNo: { list: false, form: { hidden: true } },
    whtCertFileId: { type: 'file', list: false, form: { hidden: true }, import: false, export: false },
    status: {
      readOnly: true,
      type: 'select',
      options: refundStatuses,
      filter: true,
      tones: { draft: 'neutral', requested: 'warning', approved: 'info', paid: 'success', cancelled: 'danger' },
      list: { width: 130, priority: 1 },
    },
    requestedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    approvedByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    approvedAt: { readOnly: true, type: 'datetime', list: false },
    paidByUserId: { readOnly: true, type: 'ref', ref: { resource: 'admin.user' }, list: { priority: 3, hidden: true } },
    paidAt: { readOnly: true, type: 'datetime', filter: true, list: { width: 160, priority: 2 } },
  },
  title: 'number',
  columns: ['number', 'waybillId', 'payeeName', 'amount', 'method', 'status', 'kind', 'paidAt', 'branchId', 'side', 'reason'],
  sections: ['main', 'payout'],
  defaultSort: '-createdAt',
  // `approve`: someone other than who asked; `pay`: hand the money over once approved
  actions: ['read', 'create', 'update', 'delete', 'export', 'print', 'approve', 'pay'],
  branchFields: ['branchId'],
  // Nothing is changed once asked for: what was approved is what is paid (a wrong one is cancelled and asked again)
  submittable: { cancelReason: 'required', allowOnSubmit: [] },
  card: { title: 'number', subtitle: 'payeeName', meta: ['amount', 'method'], status: 'status' },
})
